import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  useTheme,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab
} from '@mui/material';
import EmptyState from '../common/EmptyState';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import {
  TrendingUp,
  Timer,
  Assessment
} from '@mui/icons-material';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const formatInsightLabel = (value) => (
  value?.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Insight'
);

const getInsightGroupTitle = (insight) => {
  if (insight?.title && insight.title.includes(':')) {
    return insight.title.split(':')[0].trim();
  }
  if (insight?.title) {
    return insight.title.trim();
  }
  if (insight?.type) {
    return formatInsightLabel(insight.type);
  }
  return 'Insight';
};

const getInsightTopicLabel = (insight) => {
  if (insight?.title && insight.title.includes(':')) {
    const suffix = insight.title.split(':').slice(1).join(':').trim();
    if (suffix) return suffix;
  }
  if (Array.isArray(insight?.topics) && insight.topics.length > 0) {
    return insight.topics
      .map((topicItem) => topicItem?.topic)
      .filter(Boolean)
      .join(', ');
  }
  return null;
};

const AnalyticsDashboard = ({
  analyticsData,
  fromDate,
  toDate,
  selectedTopic,
  selectedExamMode,
  demoMode,
  onFromDateChange,
  onToDateChange,
  onTopicChange,
  onExamModeChange,
  availableTopics,
  availableExamModes
}) => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);

  if (!analyticsData) {
    return null;
  }

  const { 
    overall_performance, 
    topic_difficulty_breakdown, 
    confidence_analysis, 
    engagement_behavior, 
    actionable_nudges,
    comprehensive_insights
  } = analyticsData || {};

  // Add safety checks for nested objects
  const safeOverallPerformance = overall_performance || {};
  const safeTopicBreakdown = topic_difficulty_breakdown || {};
  const safeConfidenceAnalysis = confidence_analysis || {};
  const safeEngagementBehavior = engagement_behavior || {};
  const safeActionableNudges = actionable_nudges || [];
  const safeComprehensiveInsights = comprehensive_insights || [];
  const groupedComprehensiveInsights = (() => {
    if (!Array.isArray(safeComprehensiveInsights) || safeComprehensiveInsights.length === 0) {
      return [];
    }

    const priorityRank = { high: 3, medium: 2, low: 1 };
    const grouped = new Map();

    safeComprehensiveInsights.forEach((insight, index) => {
      const groupTitle = getInsightGroupTitle(insight);
      const key = `${insight?.type || groupTitle}|${insight?.category || ''}|${insight?.priority || ''}|${groupTitle}`;
      const topicLabel = getInsightTopicLabel(insight);

      if (!grouped.has(key)) {
        grouped.set(key, {
          ...insight,
          groupTitle,
          topicsList: [],
          messages: [],
          details: [],
          studentActions: [],
          teacherActions: [],
          originalOrder: index,
        });
      }

      const currentGroup = grouped.get(key);
      if ((priorityRank[insight?.priority] || 0) > (priorityRank[currentGroup.priority] || 0)) {
        currentGroup.priority = insight.priority;
      }
      if (topicLabel && !currentGroup.topicsList.includes(topicLabel)) {
        currentGroup.topicsList.push(topicLabel);
      }
      if (insight?.message && !currentGroup.messages.includes(insight.message)) {
        currentGroup.messages.push(insight.message);
      }
      if (insight?.detail && !currentGroup.details.includes(insight.detail)) {
        currentGroup.details.push(insight.detail);
      }
      if (insight?.action && !currentGroup.studentActions.includes(insight.action)) {
        currentGroup.studentActions.push(insight.action);
      }
      if (insight?.teacher_action && !currentGroup.teacherActions.includes(insight.teacher_action)) {
        currentGroup.teacherActions.push(insight.teacher_action);
      }
      if (Array.isArray(insight?.topics) && insight.topics.length > 0) {
        currentGroup.topics = currentGroup.topics || [];
        insight.topics.forEach((topicItem) => {
          const exists = currentGroup.topics.some(
            (existing) => existing?.topic === topicItem?.topic && existing?.percentage === topicItem?.percentage
          );
          if (!exists) {
            currentGroup.topics.push(topicItem);
          }
        });
      }
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        message: group.messages[0] || group.message,
        detail: group.details[0] || group.detail,
        action: group.studentActions[0] || group.action,
        teacher_action: group.teacherActions[0] || group.teacher_action,
      }))
      .sort((a, b) => {
        const priorityDiff = (priorityRank[b.priority] || 0) - (priorityRank[a.priority] || 0);
        if (priorityDiff !== 0) return priorityDiff;
        return a.originalOrder - b.originalOrder;
      });
  })();
  
  // Debug logging
  if (currentTab === 4) {
    console.log('[AnalyticsDashboard] Insights Tab - comprehensive_insights:', comprehensive_insights);
    console.log('[AnalyticsDashboard] Insights Tab - safeComprehensiveInsights:', safeComprehensiveInsights);
    console.log('[AnalyticsDashboard] Insights Tab - actionable_nudges:', actionable_nudges);
  }

  // Get all available topics for the filter dropdown
  const getAvailableTopics = () => {
    if (!safeTopicBreakdown?.topic_mastery || !Array.isArray(safeTopicBreakdown.topic_mastery)) {
      return [];
    }
    const data = safeTopicBreakdown.topic_mastery;
    return [...new Set(data.map(item => item.topic))].sort();
  };

  // Create a mapping of which topics are actually part of each test attempt
  const getTopicTestMapping = () => {
    if (!safeTopicBreakdown?.topic_mastery || !Array.isArray(safeTopicBreakdown.topic_mastery)) {
      return new Map();
    }
    
    const data = safeTopicBreakdown.topic_mastery;
    const topicTestMap = new Map();
    
    data.forEach(item => {
      const testKey = item.test_attempt_id;
      if (!topicTestMap.has(testKey)) {
        topicTestMap.set(testKey, new Set());
      }
      
      if (item.accuracy_percentage !== undefined && item.accuracy_percentage !== null) {
        topicTestMap.get(testKey).add(item.topic);
      }
    });
    
    return topicTestMap;
  };

  // Process topic mastery data for charts with proper chronological ordering
  const getTopicMasteryData = () => {
    if (!safeTopicBreakdown?.topic_mastery || !Array.isArray(safeTopicBreakdown.topic_mastery)) {
      return [];
    }
    
    const data = safeTopicBreakdown.topic_mastery;
    
    // Data is already filtered by backend, no need to filter again
    const filteredData = data;
    
    // Get unique topics from the filtered data
    const topics = [...new Set(data.map(item => item.topic))];
    
    // Extract unique test attempts from the filtered data and sort by chronological order
    const testAttemptsWithOrder = filteredData
      .filter(item => item.test_date && item.chronological_order)
      .map(item => ({
        test_attempt_id: item.test_attempt_id,
        chronological_order: item.chronological_order,
        timeline_period: item.timeline_period,
        test_title: item.test_title,
        test_date: item.test_date
      }))
      .filter((item, index, self) => 
        index === self.findIndex(t => t.test_attempt_id === item.test_attempt_id)
      )
      .sort((a, b) => a.chronological_order - b.chronological_order);
    
    // Create timeline based on chronological test attempts
    const timeline = testAttemptsWithOrder.map((attempt, index) => {
      return {
        attempt_number: attempt.chronological_order,
        test_attempt_id: attempt.test_attempt_id,
        timeline_period: attempt.timeline_period || `Test ${attempt.chronological_order}`,
        test_title: attempt.test_title || `Test ${attempt.chronological_order}`,
        test_date: attempt.test_date,
        label: `Test ${attempt.chronological_order}`
      };
    });
    
    // Map each topic to the timeline
    const result = topics.map((topic) => {
      const topicData = filteredData.filter(item => item.topic === topic);
      
      // Map topic data to timeline based on actual test attempts
      const mappedData = timeline.map((period) => {
        // Find topic data for this exact test attempt
        const dataPoint = topicData.find(item => item.test_attempt_id === period.test_attempt_id);
        
        return {
          attempt_number: period.attempt_number,
          test_attempt_id: period.test_attempt_id,
          timeline_period: period.timeline_period,
          test_title: period.test_title,
          test_date: period.test_date,
          periodLabel: period.label,
          accuracy: dataPoint ? (dataPoint.accuracy_percentage || 0) : 0
        };
      });
      
      return {
        topic,
        data: mappedData
      };
    });
    
    return result;
  };

  // Process difficulty data for charts
  const getDifficultyData = () => {
    if (!safeTopicBreakdown?.difficulty_insights || !Array.isArray(safeTopicBreakdown.difficulty_insights)) return [];
    
    const data = safeTopicBreakdown.difficulty_insights;
    
    // Data is already filtered by backend, no need to filter again
    return data.map(item => ({
      topic: item.topic,
      basic: item.basic_accuracy,
      advanced: item.advanced_accuracy
    }));
  };

  const topicMasteryData = getTopicMasteryData();
  const difficultyData = getDifficultyData();

  // Get topic performance summary data
  const topicSummaryData = safeTopicBreakdown?.topic_performance_summary || [];
  /** Table rows: respect topic filter, sort by % correct descending (highest first). */
  const topicSummaryTableRows = (() => {
    const rows = Array.isArray(topicSummaryData) ? [...topicSummaryData] : [];
    const filtered =
      selectedTopic !== 'all'
        ? rows.filter((row) => row.topic === selectedTopic)
        : rows;
    return filtered.sort((a, b) => {
      const pctA = Number(a.accuracy_percentage ?? 0);
      const pctB = Number(b.accuracy_percentage ?? 0);
      if (pctB !== pctA) return pctB - pctA;
      const qA = Number(a.questions_attempted ?? 0);
      const qB = Number(b.questions_attempted ?? 0);
      return qB - qA;
    });
  })();
  const topicMasteryRankingData = (() => {
    const rows = Array.isArray(topicSummaryData) ? topicSummaryData : [];
    const filteredRows = selectedTopic !== 'all'
      ? rows.filter((row) => row.topic === selectedTopic)
      : rows;

    return filteredRows
      .map((row) => ({
        topic: row.topic,
        avg_score: Number(row.accuracy_percentage || 0),
        questions_attempted: Number(row.questions_attempted || 0),
      }))
      .sort((a, b) => b.avg_score - a.avg_score);
  })();

  const confidencePatternCharts = (() => {
    const confidenceRows = safeConfidenceAnalysis?.confidence_by_topic?.data;
    const masteryRows = safeTopicBreakdown?.topic_mastery;
    if (!Array.isArray(confidenceRows) || !Array.isArray(masteryRows)) {
      return { overconfidentLowScore: [], underconfidentHighScore: [] };
    }

    const allowedTopics = selectedTopic !== 'all' ? new Set([selectedTopic]) : null;
    const masteryMap = new Map();
    masteryRows.forEach((item) => {
      if (!item?.topic || item?.test_attempt_id == null) return;
      masteryMap.set(`${item.test_attempt_id}::${item.topic}`, Number(item.accuracy_percentage || 0));
    });

    const grouped = {};
    confidenceRows.forEach((row) => {
      const topics = row?.topics || {};
      Object.entries(topics).forEach(([topicName, topicData]) => {
        if (allowedTopics && !allowedTopics.has(topicName)) return;
        const totalQuestions = Number(topicData?.total_questions || 0);
        if (totalQuestions <= 0) return;

        const confidencePercent = Number(topicData?.confidence_percentage || 0);
        const accuracy = masteryMap.get(`${row.test_attempt_id}::${topicName}`);
        if (accuracy == null) return;

        if (!grouped[topicName]) {
          grouped[topicName] = {
            totalCases: 0,
            highConfidenceLowScoreCases: 0,
            highConfidenceLowScoreScores: [],
            lowConfidenceHighScoreCases: 0,
            lowConfidenceHighScoreScores: [],
          };
        }

        grouped[topicName].totalCases += 1;

        if (confidencePercent > 50 && accuracy < 30) {
          grouped[topicName].highConfidenceLowScoreCases += 1;
          grouped[topicName].highConfidenceLowScoreScores.push(accuracy);
        }

        if (confidencePercent <= 50 && accuracy > 70) {
          grouped[topicName].lowConfidenceHighScoreCases += 1;
          grouped[topicName].lowConfidenceHighScoreScores.push(accuracy);
        }
      });
    });

    const toChartData = (type) => Object.entries(grouped)
      .map(([topic, value]) => {
        const matchedCases = type === 'over'
          ? value.highConfidenceLowScoreCases
          : value.lowConfidenceHighScoreCases;
        const matchedScores = type === 'over'
          ? value.highConfidenceLowScoreScores
          : value.lowConfidenceHighScoreScores;
        const caseRate = value.totalCases > 0 ? (matchedCases / value.totalCases) * 100 : 0;
        const avgScore = matchedScores.length > 0
          ? matchedScores.reduce((sum, score) => sum + score, 0) / matchedScores.length
          : 0;

        return {
          topic,
          avg_score: Math.round(avgScore * 100) / 100,
          case_rate: Math.round(caseRate * 100) / 100,
          matched_cases: matchedCases,
          total_cases: value.totalCases,
        };
      })
      .filter((item) => item.case_rate > 50)
      .sort((a, b) => b.avg_score - a.avg_score);

    return {
      overconfidentLowScore: toChartData('over'),
      underconfidentHighScore: toChartData('under'),
    };
  })();

  return (
    <>
      {/* Date Range Selector and Topic Filter */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              label="From Date"
              type="date"
              value={fromDate.toISOString().slice(0, 10)}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                onFromDateChange(newDate);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: toDate.toISOString().slice(0, 10)
              }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="To Date"
              type="date"
              value={toDate.toISOString().slice(0, 10)}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                onToDateChange(newDate);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: fromDate.toISOString().slice(0, 10),
                max: new Date().toISOString().slice(0, 10)
              }}
            />
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="topic-filter-label">Filter by Topic</InputLabel>
              <Select
                labelId="topic-filter-label"
                value={selectedTopic}
                label="Filter by Topic"
                onChange={(e) => onTopicChange(e.target.value)}
              >
                <MenuItem value="all">All Topics</MenuItem>
                {(availableTopics || []).map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {topic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="exam-mode-filter-label">Filter by Exam Mode</InputLabel>
              <Select
                labelId="exam-mode-filter-label"
                value={selectedExamMode}
                label="Filter by Exam Mode"
                onChange={(e) => onExamModeChange(e.target.value)}
              >
                <MenuItem value="all">All Exam Modes</MenuItem>
                {(availableExamModes || []).map((examMode) => (
                  <MenuItem key={examMode.id} value={examMode.id}>
                    {examMode.mode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Quick Preset Buttons */}
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Quick Select:
          </Typography>
          <Grid container spacing={1}>
            <Grid item>
              <Chip
                label="Last 30 days"
                onClick={() => {
                  const newFromDate = new Date();
                  newFromDate.setDate(newFromDate.getDate() - 30);
                  onFromDateChange(newFromDate);
                  onToDateChange(new Date());
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item>
              <Chip
                label="Last 90 days"
                onClick={() => {
                  const newFromDate = new Date();
                  newFromDate.setDate(newFromDate.getDate() - 90);
                  onFromDateChange(newFromDate);
                  onToDateChange(new Date());
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item>
              <Chip
                label="Last 6 months"
                onClick={() => {
                  const newFromDate = new Date();
                  newFromDate.setMonth(newFromDate.getMonth() - 6);
                  onFromDateChange(newFromDate);
                  onToDateChange(new Date());
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {selectedTopic !== 'all' && topicMasteryData.length > 0
                      ? topicMasteryData[0].data.filter(item => item.accuracy > 0).length
                      : safeOverallPerformance?.total_attempts || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTopic !== 'all' ? `${selectedTopic} Tests` : 'Total Tests'}
                    {demoMode && ' (Demo)'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {selectedTopic !== 'all' && topicMasteryData.length > 0
                      ? (() => {
                          const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                          if (topicData && topicData.data.length > 0) {
                            const validScores = topicData.data.filter(item => item.accuracy > 0);
                            if (validScores.length > 0) {
                              const avgScore = validScores.reduce((sum, item) => sum + item.accuracy, 0) / validScores.length;
                              return avgScore.toFixed(1);
                            }
                          }
                          return '0.0';
                        })()
                      : safeOverallPerformance?.average_score?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTopic !== 'all' ? `${selectedTopic} Avg Score` : 'Average Score'}
                    {demoMode && ' (Demo)'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Timer color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {safeEngagementBehavior?.time_performance_correlation?.scatter_data?.length > 0 
                      ? Math.round(safeEngagementBehavior.time_performance_correlation.scatter_data[0]?.time_spent_minutes || 0)
                      : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Time (min)
                    {demoMode && ' (Demo)'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Analytics Sections */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Performance" />
          <Tab label="Topic Mastery" />
          <Tab label="Confidence" />
          <Tab label="Difficulty" />
          <Tab label="Insights" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {/* Performance Tab */}
        {currentTab === 0 && (
          <Box>
            {/* Performance Over Time Chart */}
            <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {selectedTopic !== 'all' 
                ? `${selectedTopic} Performance Over Time` 
                : 'Performance Over Time (All Topics)'}
              {demoMode && ' (Demo)'}
            </Typography>
            
            {(() => {
              // Check if there's data to display
              if (selectedTopic !== 'all' && topicMasteryData.length > 0) {
                const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                if (topicData && topicData.data.length > 0) {
                  const validData = topicData.data.filter(item => item.accuracy > 0);
                  if (validData.length === 0) {
                    return (
                      <EmptyState 
                        title="No Performance Data"
                        description={`No performance data available for ${selectedTopic} in the selected date range.`}
                      />
                    );
                  }
                }
              }
              
              // For "All Topics" view, check performance trends
              const performanceTrends = safeOverallPerformance?.performance_trends || [];
              if (!Array.isArray(performanceTrends) || performanceTrends.length === 0) {
                return (
                  <EmptyState 
                    title="No Performance Data"
                    description="No test attempts found in the selected date range. Start taking tests to see your performance over time."
                  />
                );
              }
              
                             // If there's data, show the chart
               return (
                 <Box sx={{ width: '100%' }}>
                   <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={(() => {
                    // If a specific topic is selected, use topic mastery data for performance timeline
                    if (selectedTopic !== 'all' && topicMasteryData.length > 0) {
                      const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                      if (topicData && topicData.data.length > 0) {
                        // Use the same data as the mastery chart for the selected topic
                        // Ensure sequential attempt numbers starting from 1
                        
                        const filteredData = topicData.data
                          .filter(item => {
                            // Check both possible field names
                            const accuracy = item.accuracy || item.accuracy_percentage || 0;
                            return accuracy > 0;
                          })
                          .map((item, index) => ({
                            attempt_number: index + 1, // Force sequential numbering
                            avg_score: item.accuracy || item.accuracy_percentage || 0,
                            test_title: item.test_title,
                            test_date: item.test_date
                          }));
                        return filteredData;
                      }
                    }
                    
                    // For "All Topics" view, use the performance trends data from backend
                    const performanceTrends = safeOverallPerformance?.performance_trends || [];
                    
                    if (Array.isArray(performanceTrends) && performanceTrends.length > 0) {
                      // Backend now sends all tests in chronological order
                      return performanceTrends.map((trend, index) => ({
                        attempt_number: trend.attempt_number,
                        avg_score: trend.avg_score,
                        test_title: trend.test_title,
                        test_date: trend.test_date,
                        has_attempt: trend.has_attempt || true
                      }));
                    }
                    
                    // If no performance trends, return empty array to show "No data found"
                    return [];
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="attempt_number"
                      type="number"
                      domain={[1, (() => {
                        // If a specific topic is selected, use the topic data length
                        if (selectedTopic !== 'all' && topicMasteryData.length > 0) {
                          const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                          if (topicData && topicData.data.length > 0) {
                            const validDataLength = topicData.data.filter(item => item.accuracy > 0).length;
                            return validDataLength > 0 ? validDataLength : 1;
                          }
                        }
                        
                        // For "All Topics" view, use the performance trends data length
                        const performanceTrends = safeOverallPerformance?.performance_trends || [];
                        if (Array.isArray(performanceTrends) && performanceTrends.length > 0) {
                          return performanceTrends.length;
                        }
                        // If no data, return 1 to show empty chart
                        return 1;
                      })()]}
                      tickCount={(() => {
                        // If a specific topic is selected, use the topic data length
                        if (selectedTopic !== 'all' && topicMasteryData.length > 0) {
                          const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                          if (topicData && topicData.data.length > 0) {
                            const validDataLength = topicData.data.filter(item => item.accuracy > 0).length;
                            return validDataLength > 0 ? validDataLength : 1;
                          }
                        }
                        
                        // For "All Topics" view, use the performance trends data length
                        const performanceTrends = safeOverallPerformance?.performance_trends || [];
                        if (Array.isArray(performanceTrends) && performanceTrends.length > 0) {
                          return performanceTrends.length;
                        }
                        // If no data, return 1 to show empty chart
                        return 1;
                        })()}
                      allowDecimals={false}
                      label={{ value: 'Attempt', position: 'insideBottomLeft', offset: -5, dy: 10 }}
                    />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0] && payload[0].payload.test_date) {
                          return `Attempt ${value} - ${payload[0].payload.test_date}`;
                        }
                        if (payload && payload[0] && payload[0].payload.test_title) {
                          return `Attempt ${value} - ${payload[0].payload.test_title}`;
                        }
                        return `Attempt ${value}`;
                      }}
                      formatter={(value, name) => [value, 'Score (%)']}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const topicTestMap = getTopicTestMapping();
                          
                          // If a specific topic is selected, use the same filtering logic as Topic Mastery
                          if (selectedTopic !== 'all' && topicMasteryData.length > 0) {
                            const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                            if (topicData && topicData.data.length > 0) {
                              // Find the data point for this specific test attempt
                              const testData = topicData.data.find(d => d.attempt_number === label);
                              if (testData) {
                                return (
                                  <Box sx={{
                                    backgroundColor: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '10px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                  }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                      {data.test_title || `Test ${label}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      {data.test_date}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                      <Box 
                                        sx={{ 
                                          width: 12, 
                                          height: 12, 
                                          borderRadius: '50%', 
                                          backgroundColor: COLORS[0],
                                          mr: 1 
                                        }} 
                                      />
                                      <Typography variant="body2">
                                        {selectedTopic}: {testData.accuracy || testData.accuracy_percentage || 0}%
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }
                            }
                          }
                          
                          // For "All Topics" view, show the performance data
                          return (
                            <Box sx={{
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '10px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {data.test_title || `Test ${label}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {data.test_date}
                              </Typography>
                              <Typography variant="body2">
                                Score: {data.avg_score}%
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                                         <Legend 
                       verticalAlign="top" 
                       wrapperStyle={{ paddingBottom: '20px' }}
                     />
                     <Line 
                       type="monotone" 
                       dataKey="avg_score" 
                       stroke={theme.palette.primary.main} 
                       strokeWidth={2}
                       name="Average Score"
                       connectNulls={false}
                       isAnimationActive={false}
                     />
                                     </LineChart>
                 </ResponsiveContainer>
                 <Typography 
                   variant="body2" 
                   color="text.secondary" 
                   align="center" 
                   sx={{ mt: 1, fontWeight: 'medium' }}
                 >
                   Test Attempts
                 </Typography>
                 </Box>
               );
             })()}
            
            {/* Footnote */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic', px: 2, pb: 1 }}>
              💡 This chart shows your test performance progression over time. Each point represents a test attempt, 
              with the score on the Y-axis and attempt number on the X-axis. Higher scores indicate better performance.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

            {/* Topic Performance Summary Table */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Topic Performance Summary {selectedTopic !== 'all' ? `- ${selectedTopic}` : '(All Topics)'}
                  {demoMode && ' (Demo)'}
                </Typography>
                {topicSummaryTableRows.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Topic</strong></TableCell>
                          <TableCell><strong>% Correct</strong></TableCell>
                          <TableCell><strong>Questions Attempted</strong></TableCell>
                          <TableCell><strong>Last Attempt</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topicSummaryTableRows.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.topic}</TableCell>
                            <TableCell>{Number(row.accuracy_percentage ?? 0).toFixed(1)}%</TableCell>
                            <TableCell>{row.questions_attempted}</TableCell>
                            <TableCell>{row.last_attempt}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <EmptyState 
                    title="No Topic Performance Data"
                    description="No topic performance data available for the selected filters."
                  />
                )}
                
                {/* Footnote */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic', px: 2, pb: 1 }}>
                  💡 This table shows your performance summary across topics, sorted by % correct (highest first). 
                  When two topics tie, more questions attempted is listed first.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Topic Mastery Tab */}
        {currentTab === 1 && (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedTopic !== 'all' ? `${selectedTopic} Topic Mastery` : 'Topic Mastery by Average Score'}
                  {demoMode && ' (Demo)'}
                </Typography>

                {topicMasteryRankingData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(320, topicMasteryRankingData.length * 52)}>
                    <BarChart data={topicMasteryRankingData} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis type="category" dataKey="topic" width={220} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value, name) => [name === 'avg_score' ? `${value}%` : value, name === 'avg_score' ? 'Average Score' : name]}
                        labelFormatter={(label) => `Topic: ${label}`}
                      />
                      <Bar dataKey="avg_score" fill={theme.palette.primary.main} radius={[0, 6, 6, 0]} name="Average Score" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="No Topic Mastery Data"
                    description="No topic mastery data is available for the selected filters."
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic', px: 2, pb: 1 }}>
                  Topic mastery is ranked by average score from highest to lowest so you can quickly compare how each topic is going.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Legacy Topic Mastery Tab */}
        {currentTab === 101 && (
          <Box>
            {/* Topic Mastery Chart - Full Width */}
            <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {selectedTopic !== 'all' 
              ? `${selectedTopic} Mastery Progression` 
              : 'Topic Mastery Progression (All Topics)'}
            {demoMode && ' (Demo)'}
          </Typography>
          
                                                                                                                                                                                                                                                                       {topicMasteryData.length > 0 ? (
                 <Box sx={{ height: 600, width: '100%' }}>
                   <ResponsiveContainer width="100%" height={550}>
               <LineChart data={(() => {
                  // Create a flattened data structure for the chart
                  const flattenedData = [];
                  
                  // If a specific topic is selected, use only the data for that topic
                  if (selectedTopic !== 'all') {
                    const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                    if (topicData && topicData.data.length > 0) {
                      // Use only the test attempts where this topic has data
                      const validData = topicData.data.filter(item => item.accuracy > 0);
                      return validData.map((item, index) => ({
                        attempt_number: index + 1, // Sequential numbering for the selected topic
                        test_attempt_id: item.test_attempt_id,
                        timeline_period: item.timeline_period || `Test ${index + 1}`,
                        test_title: item.test_title || `Test ${index + 1}`,
                        test_date: item.test_date,
                        periodLabel: `Test ${index + 1}`,
                        [selectedTopic]: item.accuracy
                      }));
                    }
                  }
                  
                  // For "All Topics" view, use the actual timeline length from the data
                  const actualTimelineLength = topicMasteryData.length > 0 ? topicMasteryData[0].data.length : 0;
                  
                  for (let i = 0; i < actualTimelineLength; i++) {
                    const dataPoint = {
                      attempt_number: topicMasteryData[0]?.data[i]?.attempt_number || i + 1,
                      test_attempt_id: topicMasteryData[0]?.data[i]?.test_attempt_id || i + 1,
                      timeline_period: topicMasteryData[0]?.data[i]?.timeline_period || `Test ${i + 1}`,
                      test_title: topicMasteryData[0]?.data[i]?.test_title || `Test ${i + 1}`,
                      test_date: topicMasteryData[0]?.data[i]?.test_date || '',
                      periodLabel: topicMasteryData[0]?.data[i]?.periodLabel || `Test ${i + 1}`
                    };
                    
                    // Add data for each topic
                    topicMasteryData.forEach(topic => {
                      const topicDataPoint = topic.data[i];
                      dataPoint[topic.topic] = topicDataPoint ? topicDataPoint.accuracy : 0;
                    });
                    
                    flattenedData.push(dataPoint);
                  }
                  
                  return flattenedData;
                })()}>
                <CartesianGrid strokeDasharray="3 3" />
                                 <XAxis 
                   dataKey="attempt_number" 
                   type="number"
                   domain={[1, (() => {
                     // If a specific topic is selected, use the number of valid attempts for that topic
                     if (selectedTopic !== 'all') {
                       const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                       if (topicData && topicData.data.length > 0) {
                         const validDataLength = topicData.data.filter(item => item.accuracy > 0).length;
                         return validDataLength > 0 ? validDataLength : 1;
                       }
                     }
                     // For "All Topics" view, use the actual timeline length
                     return topicMasteryData.length > 0 ? topicMasteryData[0].data.length : 5;
                   })()]}
                   tickCount={(() => {
                     // If a specific topic is selected, use the number of valid attempts for that topic
                     if (selectedTopic !== 'all') {
                       const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                       if (topicData && topicData.data.length > 0) {
                         const validDataLength = topicData.data.filter(item => item.accuracy > 0).length;
                         return validDataLength > 0 ? validDataLength : 1;
                       }
                     }
                     // For "All Topics" view, use the actual timeline length
                     return topicMasteryData.length > 0 ? topicMasteryData[0].data.length : 5;
                   })()}
                 />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  formatter={(value, name) => [`${value}%`, name]}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0] && payload[0].payload.test_title) {
                      return `${payload[0].payload.test_title} (${payload[0].payload.test_date})`;
                    }
                    return `Test ${label}`;
                  }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const topicTestMap = getTopicTestMapping();
                      
                      // Only show topics that are actually part of this specific test
                      const presentTopics = payload
                        .filter(item => {
                          // Check if this topic actually has data for this specific test
                          const hasTopicData = item.value !== null && 
                                              item.value !== undefined && 
                                              !isNaN(item.value);
                          
                          // Check if this topic exists in the backend data for this specific test
                          let topicExistsInBackend = false;
                          if (item.payload && item.payload.test_attempt_id) {
                            const testAttemptId = item.payload.test_attempt_id;
                            const topicName = item.name;
                            
                            // Use the topic test mapping to check if this topic belongs to this test
                            topicExistsInBackend = topicTestMap.has(testAttemptId) && 
                                                 topicTestMap.get(testAttemptId).has(topicName);
                          }
                          
                          return hasTopicData && topicExistsInBackend;
                        })
                        .map(item => {
                          // Find the topic index to get the correct color
                          const topicIndex = topicMasteryData.findIndex(t => t.topic === item.name);
                          const color = topicIndex >= 0 ? COLORS[topicIndex % COLORS.length] : COLORS[0];
                          
                          return {
                            name: item.name,
                            value: item.value,
                            color: color
                          };
                        });
                    
                      // If no topics have any data at all, show a message
                      if (presentTopics.length === 0) {
                        return (
                          <Box sx={{
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '10px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {data.test_title || `Test ${label}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {data.test_date}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              No topic data available for this test
                            </Typography>
                          </Box>
                        );
                      }
                      
                      return (
                        <Box sx={{
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '10px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {data.test_title || `Test ${label}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {data.test_date}
                          </Typography>
                          {presentTopics.map((topic, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Box 
                                sx={{ 
                                  width: 12, 
                                  height: 12, 
                                  borderRadius: '50%', 
                                  backgroundColor: topic.color,
                                  mr: 1 
                                }} 
                              />
                              <Typography variant="body2">
                                {topic.name}: {topic.value}%
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                                 <Legend 
                   verticalAlign="top" 
                   wrapperStyle={{ paddingBottom: '20px' }}
                 />
                 {(() => {
                   // If a specific topic is selected, only show that topic
                   if (selectedTopic !== 'all') {
                     const topicData = topicMasteryData.find(t => t.topic === selectedTopic);
                     if (topicData) {
                       return (
                         <Line
                           key={topicData.topic}
                           type="monotone"
                           dataKey={topicData.topic}
                           name={topicData.topic}
                           stroke={COLORS[0]}
                           strokeWidth={2}
                           dot={{ r: 4 }}
                           connectNulls={false}
                           isAnimationActive={false}
                         />
                       );
                     }
                   }
                   
                   // For "All Topics" view, show all topics
                   return topicMasteryData.map((topic, index) => (
                     <Line
                       key={topic.topic}
                       type="monotone"
                       dataKey={topic.topic}
                       name={topic.topic}
                       stroke={COLORS[index % COLORS.length]}
                       strokeWidth={2}
                       dot={{ r: 4 }}
                       connectNulls={false}
                       isAnimationActive={false}
                     />
                   ));
                 })()}
              </LineChart>
                </ResponsiveContainer>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  align="center" 
                  sx={{ mt: 1, fontWeight: 'medium' }}
                >
                  Test Attempts
                </Typography>
              </Box>
          ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <Typography color="textSecondary">No topic mastery data available</Typography>
              </Box>
            )}
            
                         {/* Footnote */}
             <Typography variant="caption" color="text.secondary" sx={{ mt: 4, display: 'block', fontStyle: 'italic', px: 2, pb: 1 }}>
               💡 This chart tracks your mastery level across different topics over multiple test attempts. 
               Each colored line represents a topic, showing how your accuracy percentage changes with each test. 
               Higher percentages indicate better topic understanding.
             </Typography>
          </CardContent>
        </Card>
          </Box>
        )}

        {/* Confidence Tab */}
        {currentTab === 2 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Card sx={{ mb: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      High Confidence, Low Score
                      {demoMode && ' (Demo)'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Topics where confidence was above 50% in more than half of cases, but the average score in those cases stayed below 30%.
                    </Typography>
                    {confidencePatternCharts.overconfidentLowScore.length > 0 ? (
                      <ResponsiveContainer width="100%" height={Math.max(280, confidencePatternCharts.overconfidentLowScore.length * 52)}>
                        <BarChart data={confidencePatternCharts.overconfidentLowScore} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis type="category" dataKey="topic" width={220} tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === 'avg_score') return [`${value}%`, 'Average Score'];
                              if (name === 'case_rate') return [`${value}%`, 'Matched Cases'];
                              return [value, name];
                            }}
                            labelFormatter={(label) => `Topic: ${label}`}
                          />
                          <Bar dataKey="avg_score" fill={theme.palette.error.main} radius={[0, 6, 6, 0]} name="Average Score" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyState
                        title="No High-Confidence Low-Score Pattern"
                        description="No topics currently meet this confidence mismatch threshold."
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Card sx={{ mb: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Low Confidence, High Score
                      {demoMode && ' (Demo)'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Topics where confidence stayed at 50% or below in more than half of cases, while the average score in those cases was above 70%.
                    </Typography>
                    {confidencePatternCharts.underconfidentHighScore.length > 0 ? (
                      <ResponsiveContainer width="100%" height={Math.max(280, confidencePatternCharts.underconfidentHighScore.length * 52)}>
                        <BarChart data={confidencePatternCharts.underconfidentHighScore} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis type="category" dataKey="topic" width={220} tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === 'avg_score') return [`${value}%`, 'Average Score'];
                              if (name === 'case_rate') return [`${value}%`, 'Matched Cases'];
                              return [value, name];
                            }}
                            labelFormatter={(label) => `Topic: ${label}`}
                          />
                          <Bar dataKey="avg_score" fill={theme.palette.success.main} radius={[0, 6, 6, 0]} name="Average Score" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyState
                        title="No Low-Confidence High-Score Pattern"
                        description="No topics currently meet this confidence mismatch threshold."
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Legacy Confidence Tab */}
        {currentTab === 102 && (
          <Box>
            {/* Confidence Progression Chart */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Confidence Progression {selectedTopic !== 'all' ? `- ${selectedTopic}` : '(All Topics)'}
                  {demoMode && ' (Demo)'}
                </Typography>
                
                {safeConfidenceAnalysis?.confidence_by_topic?.data && safeConfidenceAnalysis.confidence_by_topic.data.length > 0 ? (
                  <Box sx={{ height: 600, width: '100%' }}>
                    <ResponsiveContainer width="100%" height={550}>
                      <LineChart data={(() => {
                        // If a specific topic is selected, filter the data to only show that topic's attempts
                        if (selectedTopic !== 'all') {
                          // Filter confidence data to only include data points that have confidence data for this topic
                          // The backend always includes all topics in each data point, so we just need to check if it has questions
                          let filteredData = safeConfidenceAnalysis.confidence_by_topic.data.filter(dataPoint => {
                            // Check if this data point has confidence data for the selected topic
                            if (!dataPoint.topics || !dataPoint.topics[selectedTopic]) {
                              return false;
                            }
                            const topicConfidence = dataPoint.topics[selectedTopic];
                            // Include if there are questions (total_questions > 0)
                            // The backend sets topics to 0 if not present, so > 0 means the topic was in this test
                            return topicConfidence.total_questions > 0;
                          });
                          
                          // Re-number the chronological order to be sequential
                          return filteredData.map((dataPoint, index) => ({
                            ...dataPoint,
                            chronological_order: index + 1
                          }));
                        }
                        
                        // For "All Topics" view, use the original data
                        return safeConfidenceAnalysis.confidence_by_topic.data;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="chronological_order"
                          type="number"
                          domain={[1, (() => {
                            // If a specific topic is selected, calculate based on filtered data
                            if (selectedTopic !== 'all') {
                              // Filter data the same way as the chart data
                              const filteredData = safeConfidenceAnalysis.confidence_by_topic.data.filter(dataPoint => {
                                return dataPoint.topics && 
                                       dataPoint.topics[selectedTopic] && 
                                       dataPoint.topics[selectedTopic].total_questions > 0;
                              });
                              return filteredData.length > 0 ? filteredData.length : 1;
                            }
                            // For "All Topics" view, use the original data length
                            return safeConfidenceAnalysis.confidence_by_topic.data.length;
                          })()]}
                          tickCount={(() => {
                            // If a specific topic is selected, calculate based on filtered data
                            if (selectedTopic !== 'all') {
                              // Filter data the same way as the chart data
                              const filteredData = safeConfidenceAnalysis.confidence_by_topic.data.filter(dataPoint => {
                                return dataPoint.topics && 
                                       dataPoint.topics[selectedTopic] && 
                                       dataPoint.topics[selectedTopic].total_questions > 0;
                              });
                              return filteredData.length > 0 ? filteredData.length : 1;
                            }
                            // For "All Topics" view, use the original data length
                            return safeConfidenceAnalysis.confidence_by_topic.data.length;
                          })()}
                        />
                        <YAxis domain={[0, 100]} />
                        <Tooltip 
                          formatter={(value, name) => [`${value}%`, 'Confidence']}
                          labelFormatter={(label) => `Test ${label}`}
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '10px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {data.test_title || `Test ${label}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {data.test_date}
                                  </Typography>
                                  {(safeConfidenceAnalysis.confidence_by_topic?.topics || []).map((topic, index) => {
                                    const topicData = data.topics?.[topic];
                                    if (topicData && topicData.total_questions > 0) {
                                      return (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                          <Box 
                                            sx={{ 
                                              width: 12, 
                                              height: 12, 
                                              borderRadius: '50%', 
                                              backgroundColor: COLORS[index % COLORS.length],
                                              mr: 1 
                                            }} 
                                          />
                                          <Typography variant="body2">
                                            {topic}: {topicData.confidence_percentage}%
                                          </Typography>
                                        </Box>
                                      );
                                    }
                                    return null;
                                  })}
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend />
                        {(() => {
                          // If a specific topic is selected, show only that topic
                          if (selectedTopic !== 'all') {
                            const topicIndex = safeConfidenceAnalysis.confidence_by_topic.topics.indexOf(selectedTopic);
                            if (topicIndex >= 0) {
                              return (
                                <Line
                                  key={selectedTopic}
                                  type="monotone"
                                  dataKey={`topics.${selectedTopic}.confidence_percentage`}
                                  name={selectedTopic}
                                  stroke={COLORS[0]}
                                  strokeWidth={2}
                                  dot={{ r: 4 }}
                                  connectNulls={false}
                                  isAnimationActive={false}
                                />
                              );
                            }
                          }
                          
                          // For "All Topics" view, show all topics
                          return (safeConfidenceAnalysis.confidence_by_topic?.topics || []).map((topic, index) => (
                            <Line
                              key={topic}
                              type="monotone"
                              dataKey={`topics.${topic}.confidence_percentage`}
                              name={topic}
                              stroke={COLORS[index % COLORS.length]}
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              connectNulls={false}
                              isAnimationActive={false}
                            />
                          ));
                        })()}
                      </LineChart>
                    </ResponsiveContainer>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      align="center" 
                      sx={{ mt: 1, fontWeight: 'medium' }}
                    >
                      Test Attempts
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <Typography color="textSecondary">No confidence progression data available</Typography>
                  </Box>
                )}
                
                {/* Footnote */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 4, display: 'block', fontStyle: 'italic', px: 2, pb: 1 }}>
                  💡 This chart displays your confidence level progression across topics over test attempts. 
                  Each line shows how confident you felt about your answers for a specific topic. 
                  Confidence is measured as the percentage of questions you marked as "Yes" when asked if you were sure about your answer.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Difficulty Tab */}
        {currentTab === 3 && (
          <Box>
            {/* Difficulty Performance Chart - Full Width */}
            <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {selectedTopic !== 'all' 
              ? `${selectedTopic} Difficulty Performance` 
              : 'Difficulty Performance by Topic'}
            {demoMode && ' (Demo)'}
          </Typography>
          {difficultyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={difficultyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="topic" />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Accuracy']}
                  labelFormatter={(label) => `Topic: ${label}`}
                />
                <Legend />
                <Bar dataKey="basic" fill="#8884d8" name="Basic" />
                <Bar dataKey="advanced" fill="#82ca9d" name="Advanced" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <Typography color="textSecondary">No difficulty data available</Typography>
            </Box>
          )}
          
          {/* Footnote */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic', px: 2, pb: 1 }}>
            💡 This chart compares your performance on basic vs. advanced difficulty questions for each topic. 
            Basic questions test fundamental concepts, while advanced questions require deeper understanding. 
            Higher percentages indicate better performance at each difficulty level.
          </Typography>
        </CardContent>
      </Card>
          </Box>
        )}

        {/* Insights Tab */}
        {currentTab === 4 && (
          <Box>
            {/* Comprehensive Insights */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Comprehensive Actionable Insights
                {demoMode && ' (Demo)'}
              </Typography>
              
              {groupedComprehensiveInsights.length > 0 ? (
                <Grid container spacing={3}>
                  {groupedComprehensiveInsights.map((insight, index) => (
                    <Grid item xs={12} md={6} lg={4} key={index}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          height: '100%',
                          borderLeft: `4px solid ${
                            insight.priority === 'high' ? theme.palette.error.main :
                            insight.priority === 'medium' ? theme.palette.warning.main :
                            theme.palette.success.main
                          }`,
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip 
                              label={insight.priority?.toUpperCase() || 'INFO'} 
                              size="small"
                              color={
                                insight.priority === 'high' ? 'error' :
                                insight.priority === 'medium' ? 'warning' :
                                'success'
                              }
                              sx={{ 
                                mr: 1,
                                color: 'white',
                                fontWeight: 'bold',
                                '& .MuiChip-label': {
                                  color: 'white !important'
                                }
                              }}
                            />
                            {insight.category && (
                              <Chip 
                                label={insight.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.7rem',
                                  color: theme.palette.text.primary,
                                  borderColor: theme.palette.divider,
                                  '& .MuiChip-label': {
                                    color: theme.palette.text.primary
                                  }
                                }}
                              />
                            )}
                          </Box>
                          
                          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: '1.1rem' }}>
                            {insight.groupTitle || insight.title || formatInsightLabel(insight.type)}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '3em' }}>
                            {insight.message}
                          </Typography>

                          {insight.topicsList && insight.topicsList.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1, color: theme.palette.text.primary }}>
                                Topics grouped here:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {insight.topicsList.map((topic, topicIndex) => (
                                  <Chip
                                    key={`${topic}-${topicIndex}`}
                                    label={topic}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.72rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {insight.detail && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              mb: 1.5,
                              fontStyle: 'italic',
                              backgroundColor: theme.palette.grey[100],
                              color: theme.palette.text.primary,
                              p: 1,
                              borderRadius: 1,
                              fontWeight: 500
                            }}>
                              📊 {insight.detail}
                            </Typography>
                          )}
                          
                          {insight.topics && Array.isArray(insight.topics) && insight.topics.length > 0 && (
                            <Box sx={{ 
                              mt: 1.5, 
                              mb: 1.5,
                              p: 1.5,
                              backgroundColor: theme.palette.error.light || '#ffebee',
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.error.main || '#f44336'}`
                            }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1, color: theme.palette.error.dark || '#c62828' }}>
                                Struggling Topics (Last 3 Tests):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {insight.topics.map((topicData, idx) => (
                                  <Chip
                                    key={idx}
                                    label={`${topicData.topic}: ${topicData.percentage}% (${topicData.count}/${topicData.total})`}
                                    size="small"
                                    sx={{
                                      backgroundColor: theme.palette.error.main || '#f44336',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      '& .MuiChip-label': {
                                        color: 'white !important'
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                              Student Action:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              💡 {insight.action}
                            </Typography>
                            
                            {insight.teacher_action && (
                              <>
                                <Typography variant="caption" color="secondary" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                  Teacher Action:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  backgroundColor: theme.palette.info?.light || theme.palette.secondary.light || '#e3f2fd',
                                  color: theme.palette.info?.contrastText || theme.palette.text.primary || '#000',
                                  p: 1,
                                  borderRadius: 1,
                                  fontSize: '0.85rem',
                                  fontWeight: 500
                                }}>
                                  👨‍🏫 {insight.teacher_action}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : safeActionableNudges && Array.isArray(safeActionableNudges) && safeActionableNudges.length > 0 ? (
                // Fallback to legacy nudges if comprehensive insights not available
                <Grid container spacing={2}>
                  {safeActionableNudges.map((nudge, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined" sx={{ 
                        borderLeft: `4px solid ${
                          nudge.priority === 'high' ? theme.palette.error.main :
                          nudge.priority === 'medium' ? theme.palette.warning.main :
                          theme.palette.success.main
                        }`
                      }}>
                        <CardContent>
                          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                            {nudge.type?.replace('_', ' ').toUpperCase() || 'INSIGHT'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {nudge.message}
                          </Typography>
                          <Typography variant="caption" color="primary" fontWeight="medium">
                            💡 {nudge.action}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <EmptyState 
                  title="No Actionable Insights"
                  description="No actionable insights available at the moment. Continue taking tests to receive personalized recommendations for improvement."
                />
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AnalyticsDashboard;
